// Rep landing — what they're earning (commission this month, what's on its way and where it's stuck)
// and who to call today, ranked by what each call is worth to them. Plus promotions their clients are
// close to, what to promote (focus products, new arrivals, online hits), and their latest orders. Commission details
// (monthly reports to approve, history) stay at the bottom. Reps don't see the staff dashboard.
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import StatusBadge from '../components/StatusBadge.jsx';
import { RepCommissions } from './Commissions.jsx';
import { useAuthStore } from '../stores/authStore.js';
import { fmtMoney } from '../lib/matrix.js';
import { fetchRepHome } from '../api/me.js';
import { fetchWhoIsOnline } from '../api/presence.js';
import LiveClients from '../components/LiveClients.jsx';

const KIND = {
  overdue: { label: 'Overdue', cls: 'bg-error-light text-error' },
  reorder: { label: 'Due to reorder', cls: 'bg-orange-50 text-orange-700' },
  promo: { label: 'Near a promo tier', cls: 'bg-info-light text-info' },
  quiet: { label: 'Gone quiet', cls: 'bg-surface-raised text-text-secondary' },
  first: { label: 'No first order yet', cls: 'bg-violet-50 text-violet-700' },
};
const ORDER_KINDS = new Set(['reorder', 'promo', 'first']);
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const audWhole = (n) => `A$${Math.round(Number(n) || 0).toLocaleString('en-AU')}`;
const initials = (name) =>
  String(name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
const greeting = () => {
  const h = Number(new Date().toLocaleString('en-AU', { timeZone: 'Australia/Sydney', hour: 'numeric', hour12: false }));
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
};

function MoneyHero({ c, sales }) {
  const cur = c.currency;
  const money = (v) => (v == null ? '—' : fmtMoney(v, cur));
  const tiles = [
    ['Awaiting approval', c.pipeline.placed.value, c.pipeline.placed.count ? `${c.pipeline.placed.count} order${c.pipeline.placed.count === 1 ? '' : 's'}` : 'nothing pending', 'bg-surface-raised'],
    ['Invoiced, not yet paid', c.pipeline.invoiced.value, 'paid to you once they pay', 'bg-info-light/60'],
    ['Overdue', c.pipeline.overdue.value, c.pipeline.overdue.clients ? `${c.pipeline.overdue.clients} client${c.pipeline.overdue.clients === 1 ? '' : 's'} late` : 'nobody late', 'bg-error-light/60'],
    ['Earned this month', c.earned, 'from payments received', 'bg-success-light/60'],
  ];
  return (
    <section aria-label="Your commission" className="flex flex-wrap gap-6 rounded-2xl border border-line bg-surface p-5 shadow-sm">
      <div className="flex-[1_1_260px]">
        <div className="text-xs font-semibold uppercase tracking-wide text-text-muted">Your commission · {c.month ? `${MONTHS[c.month.month - 1]} so far` : 'this month'}</div>
        <div className="mt-1 text-4xl font-extrabold tabular-nums tracking-tight text-text-primary sm:text-5xl">{money(c.earned)}</div>
        <div className="mt-1 text-sm text-text-secondary">{c.rate != null ? `${c.rate}% of ${fmtMoney(c.base, cur)} your clients have paid` : 'Your commission rate hasn’t been set yet'}</div>
        {c.onTheWay > 0 && <div className="mt-2 text-sm font-semibold text-text-primary">+ {money(c.onTheWay)} on the way as clients pay</div>}
        {sales.target ? (
          <div className="mt-4">
            <div className="flex justify-between text-[13px]">
              <span className="font-semibold text-text-primary">Sales {audWhole(sales.value)}</span>
              <span className="text-text-muted">target {audWhole(sales.target)}</span>
            </div>
            <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-surface-raised">
              <div className="h-full rounded-full bg-accent" style={{ width: `${Math.min(100, sales.pctOfTarget || 0)}%` }} />
            </div>
            <div className={`mt-1 text-xs ${sales.projected >= sales.target ? 'text-success' : 'text-text-muted'}`}>
              {sales.pctOfTarget}% of your monthly target · on pace for {audWhole(sales.projected)}
            </div>
          </div>
        ) : (
          <div className="mt-3 text-[13px] text-text-muted">Your sales this month: <strong className="text-text-primary">{audWhole(sales.value)}</strong></div>
        )}
      </div>
      {/* On a phone the call list matters more than the breakdown — the "on the way" line covers it. */}
      <div className="hidden min-w-0 flex-[2_1_480px] sm:block">
        <div className="mb-2 text-[13px] font-semibold text-text-secondary">Where your next commission is coming from</div>
        <div className="grid grid-cols-2 gap-2.5 lg:grid-cols-4">
          {tiles.map(([label, v, note, bg]) => (
            <div key={label} className={`rounded-xl p-3 ${bg}`}>
              <div className="text-xs font-semibold text-text-secondary">{label}</div>
              <div className="mt-1 text-xl font-extrabold tabular-nums text-text-primary">{money(v)}</div>
              <div className="mt-0.5 text-xs text-text-secondary">{note}</div>
            </div>
          ))}
        </div>
        <p className="mt-2 text-xs text-text-muted">Commission is earned when your clients pay — chasing overdue balances gets you paid sooner.</p>
      </div>
    </section>
  );
}

function CallRow({ c, currency, onAction, onPitch }) {
  const k = KIND[c.kind];
  return (
    <li className="flex flex-wrap items-center gap-3 border-t border-surface-raised px-5 py-3.5">
      <div aria-hidden="true" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-surface-raised text-sm font-bold text-text-secondary">{initials(c.name)}</div>
      <div className="min-w-0 flex-[1_1_280px]">
        <div className="flex flex-wrap items-center gap-2">
          <Link to={`/clients/${c.id}`} className="text-[15px] font-bold text-text-primary hover:underline">{c.name}</Link>
          <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${k.cls}`}>{k.label}</span>
          {c.also.map((a) => (
            <span key={a} className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${KIND[a].cls}`}>{KIND[a].label}</span>
          ))}
          {c.online && <span className="text-[11px] font-bold text-success">● on the platform now</span>}
        </div>
        {c.live && (c.live.page || c.live.cart) && (
          <div className="mt-1 text-xs font-medium text-success">
            Now: {c.live.page || 'On the platform'}
            {c.live.cart ? ` · order in progress ${fmtMoney(c.live.cart.subtotal, c.live.cart.currency)}` : ''}
          </div>
        )}
        <div className="mt-1 text-[13px] text-text-secondary">{c.why}</div>
        {c.pitch?.length > 0 && (
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-xs">
            <span className="font-semibold text-text-secondary">Pitch:</span>
            {c.pitch.map((p) => (
              <button key={p.key} type="button" onClick={() => onPitch(c, p)} title={p.why} className="rounded-full border border-line px-2 py-0.5 text-text-primary hover:border-accent">
                {p.title}
              </button>
            ))}
          </div>
        )}
        {c.contact && (
          <div className="mt-0.5 text-xs text-text-muted">
            {c.contact.name || 'Contact'}
            {c.contact.phone && (
              <>
                {' · '}
                <a href={`tel:${c.contact.phone.replace(/\s+/g, '')}`} className="hover:underline">{c.contact.phone}</a>
              </>
            )}
            {c.contact.email && (
              <>
                {' · '}
                <a href={`mailto:${c.contact.email}`} className="hover:underline">{c.contact.email}</a>
              </>
            )}
          </div>
        )}
      </div>
      <div className="min-w-[96px] text-right">
        <div className="text-[15px] font-bold tabular-nums text-text-primary">{c.value != null ? audWhole(c.value) : '—'}</div>
        <div className="text-[11px] text-text-muted">{c.valueNote}</div>
        {c.worth > 0 && <div className="text-[11px] font-semibold text-success">≈ {fmtMoney(c.worth, currency)} to you</div>}
      </div>
      <div className="flex gap-1.5">
        <button type="button" onClick={() => onAction(c)} className="whitespace-nowrap rounded-lg bg-accent px-3 py-2 text-[13px] font-bold text-white hover:bg-accent-hover">
          {c.action}
        </button>
        {c.contact?.phone && (
          <a href={`tel:${c.contact.phone.replace(/\s+/g, '')}`} className="rounded-lg border border-line-strong px-3 py-2 text-[13px] font-semibold text-text-primary hover:bg-surface-raised">
            Call
          </a>
        )}
      </div>
    </li>
  );
}

const PROMO_TABS = [
  ['focus', 'Focus from TEAMM8'],
  ['new', 'New arrivals'],
  ['online', 'Selling online'],
];

// What to promote: staff-picked focus products, new arrivals and online best sellers — and, for the
// one picked, which of the rep's clients don't stock it yet, each with "Add to their order".
function WhatToPromote({ promote, onAdd }) {
  const tabs = PROMO_TABS.filter(([k]) => promote?.[k]?.length);
  const [tab, setTab] = useState(null);
  const [picked, setPicked] = useState(0);
  if (!tabs.length) return null;
  const active = tabs.some(([k]) => k === tab) ? tab : tabs[0][0];
  const list = promote[active];
  const cur = list[Math.min(picked, list.length - 1)];
  return (
    <section aria-label="What to promote" className="overflow-hidden rounded-2xl border border-line bg-surface shadow-sm">
      <div className="px-5 pt-4">
        <h2 className="text-lg font-bold text-text-primary">What to promote</h2>
        <p className="mt-0.5 text-[13px] text-text-muted">What TEAMM8 wants pushed, what’s new and what sells online — and which of your clients don’t have it yet</p>
      </div>
      <div role="tablist" aria-label="What to promote" className="mt-2 flex flex-wrap gap-1 border-b border-line px-5">
        {tabs.map(([k, label]) => (
          <button key={k} type="button" role="tab" aria-selected={active === k} onClick={() => { setTab(k); setPicked(0); }} className={`-mb-px border-b-2 px-3 py-2.5 text-sm font-semibold ${active === k ? 'border-accent text-text-primary' : 'border-transparent text-text-muted hover:text-text-primary'}`}>
            {label} <span className="ml-1 rounded-full bg-surface-raised px-1.5 text-[11px] font-bold text-text-secondary">{promote[k].length}</span>
          </button>
        ))}
      </div>
      <div className="flex flex-wrap">
        <div className="grid min-w-0 flex-[3_1_520px] grid-cols-2 content-start gap-3 p-5 sm:grid-cols-3">
          {list.map((p, i) => (
            <button key={p.key} type="button" onClick={() => setPicked(i)} aria-pressed={cur.key === p.key} className={`flex flex-col overflow-hidden rounded-xl border-2 bg-surface text-left ${cur.key === p.key ? 'border-accent' : 'border-line hover:border-line-strong'}`}>
              <div className="relative aspect-square w-full bg-surface-raised">
                {p.image && <img src={p.image} alt="" loading="lazy" className="h-full w-full object-cover" />}
                <span className="absolute left-2 top-2 rounded-full bg-accent px-2 py-0.5 text-[11px] font-bold text-white">{p.tag}</span>
              </div>
              <span className="flex flex-col gap-0.5 p-2.5">
                <span className="line-clamp-2 text-[13px] font-bold text-text-primary">{p.title}</span>
                <span className="text-xs text-text-muted">{p.why}</span>
                <span className="mt-1 text-xs font-bold text-orange-700">{p.missingCount ? `${p.missingCount} of your ${p.stores} store${p.stores === 1 ? '' : 's'} don’t have it` : 'All your stores stock it'}</span>
              </span>
            </button>
          ))}
        </div>
        <aside aria-label="Who it’s for" className="flex min-w-0 flex-[2_1_320px] flex-col gap-3 border-t border-line bg-surface-raised/40 p-5 sm:border-l sm:border-t-0">
          <div className="flex items-center gap-3">
            {cur.image ? <img src={cur.image} alt="" className="h-14 w-14 shrink-0 rounded-lg object-cover" /> : <div className="h-14 w-14 shrink-0 rounded-lg bg-surface-raised" />}
            <div className="min-w-0">
              <div className="font-bold text-text-primary">{cur.title}</div>
              <div className="text-xs text-text-muted">{cur.store === 'A' ? 'Australia' : 'International'} · {cur.why}</div>
            </div>
          </div>
          {cur.note && (
            <p className="rounded-lg bg-orange-50 px-3 py-2 text-[13px] text-orange-900">
              <strong>From TEAMM8:</strong> {cur.note}
            </p>
          )}
          <div className="text-[13px] font-bold text-text-secondary">
            Who it’s for · {cur.missingCount} client{cur.missingCount === 1 ? '' : 's'} don’t stock it
          </div>
          <ul className="flex flex-col gap-2">
            {cur.missing.slice(0, 8).map((m) => (
              <li key={m.id} className="flex flex-wrap items-center gap-2 rounded-xl border border-line bg-surface px-3 py-2">
                <span className="min-w-0 flex-[1_1_150px]">
                  <Link to={`/clients/${m.id}`} className="block truncate text-sm font-bold text-text-primary hover:underline">{m.name}</Link>
                  <span className="text-xs text-text-muted">{m.why}</span>
                </span>
                <button type="button" onClick={() => onAdd(m.id, cur)} className="whitespace-nowrap rounded-lg bg-accent px-2.5 py-1.5 text-xs font-bold text-white hover:bg-accent-hover">
                  Add to their order
                </button>
              </li>
            ))}
          </ul>
          {cur.missing.length > 8 && <p className="text-xs text-text-muted">+ {cur.missing.length - 8} more</p>}
          {cur.carry.length > 0 && <p className="text-xs text-text-muted">Already stocked by {cur.carry.slice(0, 4).join(', ')}{cur.carry.length > 4 ? ` and ${cur.carry.length - 4} more` : ''}.</p>}
        </aside>
      </div>
    </section>
  );
}

export default function RepHome() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const [h, setH] = useState(null);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('');
  const [online, setOnline] = useState(null);
  // Their clients online right now — refreshed every 30s (the rest of the page loads once).
  useEffect(() => {
    let alive = true;
    const load = () => fetchWhoIsOnline().then((u) => alive && setOnline(u)).catch(() => alive && setOnline([]));
    load();
    const t = setInterval(load, 30e3);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);
  useEffect(() => {
    fetchRepHome()
      .then(setH)
      .catch((e) => setError(e?.response?.data?.error?.message || 'Couldn’t load your home page.'));
  }, []);

  const calls = useMemo(() => {
    const liveBy = new Map((online || []).filter((u) => u.companyId).map((u) => [u.companyId, u]));
    return (h?.calls || [])
      .filter((c) => !filter || c.kind === filter || c.also.includes(filter))
      .map((c) => (online ? { ...c, online: liveBy.has(c.id), live: liveBy.get(c.id) || null } : c));
  }, [h, filter, online]);
  // New order for a client, carrying what to pitch on the call (shown as a strip on the order screen).
  const act = (c) => {
    if (ORDER_KINDS.has(c.kind) && c.orderFor) navigate('/catalog', { state: { client: c.orderFor, suggest: c.pitch || [] } });
    else navigate(c.href);
  };
  // "Add to their order": New order for that client, opened on the product.
  const openFor = (companyId, p) => {
    const client = h?.orderFor?.[companyId];
    if (!client) return navigate(`/clients/${companyId}`);
    const pitch = h.calls.find((c) => c.id === companyId)?.pitch || [p];
    navigate(`/catalog?product=${encodeURIComponent(p.productId)}`, { state: { client, suggest: pitch } });
  };
  const firstName = h?.firstName || (user?.name || '').split(' ')[0];
  const today = new Date().toLocaleDateString('en-AU', { timeZone: 'Australia/Sydney', weekday: 'long', day: 'numeric', month: 'long' });

  return (
    <div className="mx-auto flex max-w-[1240px] flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[13px] text-text-muted">{today}</div>
          <h1 className="mt-0.5 text-2xl font-bold tracking-tight text-text-primary sm:text-[28px]">
            {greeting()}
            {firstName ? `, ${firstName}` : ''}
            {h?.callCount ? <span className="font-normal text-text-secondary"> — {h.callCount} client{h.callCount === 1 ? '' : 's'} worth a call today</span> : null}
          </h1>
        </div>
        <Link to="/catalog" className="rounded-lg bg-accent px-4 py-2.5 text-sm font-bold text-white hover:bg-accent-hover">
          + New order for a client
        </Link>
      </div>

      {error && <p className="rounded-md bg-error-light px-3 py-2 text-sm text-error">{error}</p>}
      {!h && !error && <div className="h-48 animate-pulse rounded-2xl border border-line bg-surface" />}

      {h && (
        <>
          <MoneyHero c={h.commission} sales={h.sales} />

          <div className="flex flex-wrap items-start gap-5">
            <section aria-label="Who to call today" className="min-w-0 flex-[2_1_600px] overflow-hidden rounded-2xl border border-line bg-surface shadow-sm">
              <div className="flex flex-wrap items-baseline justify-between gap-2 px-5 pb-2 pt-4">
                <div>
                  <h2 className="text-lg font-bold text-text-primary">Who to call today</h2>
                  <p className="mt-0.5 text-[13px] text-text-muted">Ranked by what it’s worth — clients on the platform right now come first</p>
                </div>
                {filter && (
                  <button type="button" onClick={() => setFilter('')} className="text-[13px] font-semibold text-text-primary hover:underline">
                    Show everyone
                  </button>
                )}
              </div>
              {calls.length === 0 ? (
                <p className="border-t border-line px-5 py-8 text-center text-sm text-text-muted">
                  {h.clients ? 'Nobody needs a call right now — everyone is on their usual rhythm and up to date.' : 'You don’t have any clients assigned yet. Ask an admin to add some to your book.'}
                </p>
              ) : (
                <ul>
                  {calls.map((c) => (
                    <CallRow key={c.id} c={c} currency={h.commission.currency} onAction={act} onPitch={(row, p) => openFor(row.id, p)} />
                  ))}
                </ul>
              )}
              {h.callCount > h.calls.length && !filter && <p className="border-t border-surface-raised px-5 py-2 text-xs text-text-muted">Showing the top {h.calls.length} of {h.callCount}.</p>}
            </section>

            <div className="flex min-w-0 flex-[1_1_320px] flex-col gap-5">
              {online?.length > 0 && <LiveClients users={online} title="Your clients online now" showRep={false} />}
              {h.moves.length > 0 && (
                <section aria-label="Earn more this month" className="overflow-hidden rounded-2xl border border-line bg-surface shadow-sm">
                  <div className="px-5 pb-1 pt-4">
                    <h2 className="text-[15px] font-bold text-text-primary">Earn more this month</h2>
                    <p className="text-xs text-text-muted">Tap one to see those clients</p>
                  </div>
                  <ul>
                    {h.moves.map((m) => (
                      <li key={m.kind} className="border-t border-surface-raised">
                        <button type="button" onClick={() => setFilter(filter === m.kind ? '' : m.kind)} aria-pressed={filter === m.kind} className={`flex w-full items-center gap-3 px-5 py-3 text-left hover:bg-surface-raised/50 ${filter === m.kind ? 'bg-surface-raised/70' : ''}`}>
                          <span className="min-w-0 flex-1">
                            <span className="block text-sm font-semibold text-text-primary">{m.title}</span>
                            <span className="block truncate text-xs text-text-muted">{m.sub}</span>
                          </span>
                          {m.worth > 0 && <span className="shrink-0 text-sm font-bold text-success">+{fmtMoney(m.worth, h.commission.currency)}</span>}
                        </button>
                      </li>
                    ))}
                  </ul>
                </section>
              )}

              {h.nearTier.length > 0 && (
                <section aria-label="Close to a promotion tier" className="rounded-2xl border border-line bg-surface p-5 shadow-sm">
                  <h2 className="text-[15px] font-bold text-text-primary">Close to a better discount</h2>
                  <p className="mb-2 text-xs text-text-muted">Their usual order vs the next promotion tier</p>
                  {h.nearTier.map((n) => (
                    <div key={n.id} className="border-t border-surface-raised py-2">
                      <div className="flex justify-between gap-2 text-[13px]">
                        <strong className="truncate text-text-primary">{n.name}</strong>
                        <span className="shrink-0 font-semibold text-orange-700">{fmtMoney(n.gap, n.currency)} to {n.percent}% off</span>
                      </div>
                      <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-surface-raised">
                        <div className="h-full rounded-full bg-orange-600" style={{ width: `${n.progress}%` }} />
                      </div>
                      <div className="mt-1 text-[11px] text-text-muted">
                        {n.promotion}
                        {n.endsAt ? ` · ends ${new Date(n.endsAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', timeZone: 'UTC' })}` : ''}
                      </div>
                    </div>
                  ))}
                </section>
              )}

              <section aria-label="Your latest orders" className="rounded-2xl border border-line bg-surface p-5 shadow-sm">
                <div className="mb-1 flex items-baseline justify-between">
                  <h2 className="text-[15px] font-bold text-text-primary">Your latest orders</h2>
                  <Link to="/orders" className="text-[13px] font-semibold text-text-primary hover:underline">All orders</Link>
                </div>
                {h.orders.length === 0 && <p className="text-sm text-text-muted">No orders yet.</p>}
                <ul>
                  {h.orders.map((o) => (
                    <li key={o.id} className="border-t border-surface-raised py-2">
                      <Link to={`/orders/${o.id}`} className="flex items-center justify-between gap-2 text-[13px]">
                        <span className="min-w-0">
                          <span className="block truncate font-semibold text-text-primary">{o.client}</span>
                          <span className="text-xs text-text-muted">{o.ref} · {new Date(o.createdAt).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })} · {fmtMoney(o.total, o.currency)}</span>
                        </span>
                        <StatusBadge status={o.status} />
                      </Link>
                    </li>
                  ))}
                </ul>
              </section>
            </div>
          </div>

          <WhatToPromote promote={h.promote} onAdd={openFor} />
        </>
      )}

      <section aria-label="Commission details" className="mt-2">
        <RepCommissions embedded />
      </section>
    </div>
  );
}
